import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const WEEKDAYS_DE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS_DE = ['Januar', 'Februar', 'Maerz', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

// Map German day names to weekday index (0=Mo, 6=So)
const DAY_MAP = {
  'montag': 0, 'montags': 0,
  'dienstag': 1, 'dienstags': 1,
  'mittwoch': 2, 'mittwochs': 2,
  'donnerstag': 3, 'donnerstags': 3,
  'freitag': 4, 'freitags': 4,
  'samstag': 5, 'samstags': 5,
  'sonntag': 6, 'sonntags': 6,
  'sunday': 6, 'monday': 0, 'tuesday': 1, 'wednesday': 2,
  'thursday': 3, 'friday': 4, 'saturday': 5,
};

const EventCalendar = ({ events = [] }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Build event weekday lookup
  const eventsByWeekday = {};
  const EVENT_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];
  events.forEach((ev, idx) => {
    // Check day_of_week, recurring, or title for day info
    const dayStr = (ev.day_of_week || ev.recurring || '').toLowerCase().trim();
    let weekdayIdx = DAY_MAP[dayStr];
    // Try extracting day name from recurring text like "Jeden Mittwoch"
    if (weekdayIdx === undefined) {
      for (const [key, val] of Object.entries(DAY_MAP)) {
        if (dayStr.includes(key)) {
          weekdayIdx = val;
          break;
        }
      }
    }
    if (weekdayIdx !== undefined) {
      if (!eventsByWeekday[weekdayIdx]) eventsByWeekday[weekdayIdx] = [];
      eventsByWeekday[weekdayIdx].push({ ...ev, color: EVENT_COLORS[idx % EVENT_COLORS.length] });
    }
  });

  // Calendar grid
  const firstDay = new Date(year, month, 1);
  let startWeekday = firstDay.getDay() - 1; // Convert Sun=0 to Mon-based
  if (startWeekday < 0) startWeekday = 6;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  // Empty cells before first day
  for (let i = 0; i < startWeekday; i++) {
    days.push({ day: null, weekday: i });
  }
  // Actual days
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    let weekday = date.getDay() - 1;
    if (weekday < 0) weekday = 6;
    days.push({ day: d, weekday, date });
  }

  const today = new Date();
  const isToday = (d) => d && d === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const dayEvents = selectedDay !== null ? (eventsByWeekday[selectedDay.weekday] || []) : [];

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-testid="event-calendar">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 bg-[#0f2440] text-white">
        <button onClick={prevMonth} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="cal-prev">
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-bold" data-testid="cal-month-label">
          {MONTHS_DE[month]} {year}
        </h3>
        <button onClick={nextMonth} className="p-1.5 hover:bg-white/10 rounded-lg transition-colors" data-testid="cal-next">
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Weekday Headers */}
      <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
        {WEEKDAYS_DE.map((wd, i) => (
          <div key={wd} className={`text-center py-2 text-xs font-semibold ${i >= 5 ? 'text-red-400' : 'text-gray-500'}`}>
            {wd}
          </div>
        ))}
      </div>

      {/* Day Grid */}
      <div className="grid grid-cols-7">
        {days.map((cell, idx) => {
          if (!cell.day) {
            return <div key={`empty-${idx}`} className="h-12 border-b border-r border-gray-50" />;
          }
          const hasEvents = !!eventsByWeekday[cell.weekday];
          const isSelected = selectedDay && selectedDay.day === cell.day;
          const isTodayCell = isToday(cell.day);

          return (
            <button
              key={cell.day}
              onClick={() => setSelectedDay(isSelected ? null : cell)}
              data-testid={`cal-day-${cell.day}`}
              className={`h-12 relative flex flex-col items-center justify-center border-b border-r border-gray-50 transition-all text-sm
                ${isSelected ? 'bg-[#0f2440] text-white' : isTodayCell ? 'bg-blue-50 font-bold text-[#0f2440]' : 'hover:bg-gray-50'}
                ${cell.weekday >= 5 && !isSelected ? 'text-gray-400' : ''}
                ${hasEvents && !isSelected ? 'font-semibold' : ''}
              `}
            >
              {cell.day}
              {hasEvents && (
                <div className="flex gap-0.5 mt-0.5">
                  {eventsByWeekday[cell.weekday].slice(0, 3).map((ev) => (
                    <div
                      key={ev.id || ev.title}
                      className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-yellow-300' : ''}`}
                      style={!isSelected ? { backgroundColor: ev.color || '#3b82f6' } : undefined}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Selected Day Events */}
      {selectedDay && dayEvents.length > 0 && (
        <div className="border-t border-gray-200 p-4 bg-blue-50/30 space-y-2" data-testid="cal-day-events">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {WEEKDAYS_DE[selectedDay.weekday]}, {selectedDay.day}. {MONTHS_DE[month]}
          </p>
          {dayEvents.map((ev) => (
            <div key={ev.id || ev.title} className="flex items-center gap-3 bg-white rounded-lg px-3 py-2.5 shadow-sm border border-gray-100">
              <div className="w-1 h-8 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color || '#3b82f6' }} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{ev.title}</p>
                <p className="text-xs text-gray-500">{ev.time || ev.time_str}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedDay && dayEvents.length === 0 && (
        <div className="border-t border-gray-200 p-4 bg-gray-50/50 text-center">
          <p className="text-sm text-gray-400">Keine Termine an diesem Tag</p>
        </div>
      )}
    </div>
  );
};

export default EventCalendar;
